import React, { useState, useEffect } from 'react';
import { DataTable } from '@/components/DataTable';
import { inventoryService } from '@/services/inventory';
import { getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Plus } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function InventoryPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [newItem, setNewItem] = useState({
    item: '',
    category: '',
    quantity: '',
    unit: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await inventoryService.getAll();
        setInventory(r.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      }
    })();
  }, []);

  const columns = [
    { key: 'item', header: 'Item', sortable: true },
    { key: 'category', header: 'Category', sortable: true },
    { key: 'quantity', header: 'Quantity', sortable: true },
    { key: 'unit', header: 'Unit', sortable: true },
  ];

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Inventory</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage hospital inventory and supplies
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-inventory-item-button">
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              Add Item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Inventory Item</DialogTitle>
              <DialogDescription>
                Add a new item to inventory
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="item-name">Item Name</Label>
                <Input
                  id="item-name"
                  placeholder="Surgical Gloves"
                  value={newItem.item}
                  onChange={(e) => setNewItem((i) => ({ ...i, item: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="item-category">Category</Label>
                <Input
                  id="item-category"
                  placeholder="Consumables"
                  value={newItem.category}
                  onChange={(e) => setNewItem((i) => ({ ...i, category: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="item-qty">Quantity</Label>
                  <Input
                    id="item-qty"
                    type="number"
                    placeholder="100"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem((i) => ({ ...i, quantity: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item-unit">Unit</Label>
                  <Input
                    id="item-unit"
                    placeholder="Boxes"
                    value={newItem.unit}
                    onChange={(e) => setNewItem((i) => ({ ...i, unit: e.target.value }))}
                  />
                </div>
              </div>
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    await inventoryService.create({
                      item: newItem.item,
                      category: newItem.category,
                      quantity: Number(newItem.quantity) || 0,
                      unit: newItem.unit,
                    });
                    toast.success('Item added successfully');
                    setIsDialogOpen(false);
                    setNewItem({ item: '', category: '', quantity: '', unit: '' });
                    const r = await inventoryService.getAll();
                    setInventory(r.data);
                  } catch (error) {
                    toast.error(getApiErrorMessage(error));
                  }
                }}
              >
                Add Item
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable data={inventory} columns={columns} searchPlaceholder="Search inventory..." />
    </div>
  );
}